import {
    Add,
    Delete,
    Edit,
    Search,
    Visibility
} from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Fade,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
    Zoom
} from '@mui/material';
import React, { useState } from 'react';

/**
 * ModernCardGrid - A creative replacement for table views
 *
 * @param {Array} data - Array of items to display
 * @param {Function} onEdit - Callback when edit is clicked
 * @param {Function} onDelete - Callback when delete is clicked
 * @param {Function} onView - Optional callback for view action
 * @param {Function} onAdd - Callback when add button is clicked
 * @param {String} searchPlaceholder - Placeholder text for search
 * @param {Function} renderCard - Custom render function for card content
 * @param {String} addButtonText - Text for the add button
 */
export default function ModernCardGrid({
    data = [],
    onEdit,
    onDelete,
    onView,
    onAdd,
    searchPlaceholder = "Search...",
    renderCard,
    addButtonText = "Add New",
    emptyMessage = "No items found"
}) {
    const [searchTerm, setSearchTerm] = useState('');
    const [hoveredCard, setHoveredCard] = useState(null);

    // Filter data based on search
    const filteredData = data.filter(item => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return Object.values(item).some(value =>
            String(value).toLowerCase().includes(searchLower)
        );
    });

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '100%',
            height: '100%'
        }}>
            {/* Header with Search and Add */}
            <Box sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between'
            }}>
                <TextField
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    sx={{
                        flex: 1,
                        minWidth: { xs: '100%', sm: '300px' },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: 'background.paper',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: 2,
                            },
                            '&.Mui-focused': {
                                boxShadow: 3,
                            }
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={onAdd}
                    sx={{
                        borderRadius: '12px',
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        boxShadow: 3,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                            boxShadow: 6,
                            transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                        width: { xs: '100%', sm: 'auto' }
                    }}
                >
                    {addButtonText}
                </Button>
            </Box>

            {/* Results Count */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                    Showing <strong>{filteredData.length}</strong> {filteredData.length === 1 ? 'item' : 'items'}
                </Typography>
                {searchTerm && (
                    <Chip
                        label={`Filtered: "${searchTerm}"`}
                        size="small"
                        onDelete={() => setSearchTerm('')}
                        sx={{ borderRadius: '8px' }}
                    />
                )}
            </Box>

            {/* Cards Grid */}
            <Box sx={{
                flex: 1,
                overflow: 'auto',
                width: '100%'
            }}>
                {filteredData.length > 0 ? (
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(3, 1fr)',
                            lg: 'repeat(4, 1fr)'
                        },
                        gap: 2,
                        pb: 2
                    }}>
                        {filteredData.map((item, index) => (
                            <Zoom
                                in={true}
                                key={item.id}
                                style={{ transitionDelay: `${index * 50}ms` }}
                            >
                                <Card
                                    onMouseEnter={() => setHoveredCard(item.id)}
                                    onMouseLeave={() => setHoveredCard(null)}
                                    sx={{
                                        position: 'relative',
                                        height: '100%',
                                        borderRadius: '16px',
                                        overflow: 'hidden',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        cursor: 'pointer',
                                        border: '1px solid',
                                        borderColor: hoveredCard === item.id ? 'primary.main' : 'divider',
                                        boxShadow: hoveredCard === item.id ? 8 : 1,
                                        transform: hoveredCard === item.id ? 'translateY(-8px)' : 'translateY(0)',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: '4px',
                                            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                                            opacity: hoveredCard === item.id ? 1 : 0,
                                            transition: 'opacity 0.3s ease'
                                        }
                                    }}
                                >
                                    <CardContent sx={{ p: 2.5 }}>
                                        {/* Custom Content */}
                                        {renderCard ? renderCard(item) : (
                                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                                {item.name}
                                            </Typography>
                                        )}

                                        {/* Actions - Visible on hover */}
                                        <Fade in={hoveredCard === item.id}>
                                            <Box sx={{
                                                display: 'flex',
                                                gap: 1,
                                                justifyContent: 'flex-end',
                                                mt: 2,
                                                pt: 2,
                                                borderTop: '1px solid',
                                                borderColor: 'divider'
                                            }}>
                                                {onView && (
                                                    <Tooltip title="View Details" arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onView(item);
                                                            }}
                                                            sx={{
                                                                bgcolor: 'info.main',
                                                                color: 'white',
                                                                '&:hover': {
                                                                    bgcolor: 'info.dark',
                                                                    transform: 'scale(1.1)'
                                                                },
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                        >
                                                            <Visibility fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {onEdit && (
                                                    <Tooltip title="Edit" arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onEdit(item);
                                                            }}
                                                            sx={{
                                                                bgcolor: 'primary.main',
                                                                color: 'white',
                                                                '&:hover': {
                                                                    bgcolor: 'primary.dark',
                                                                    transform: 'scale(1.1)'
                                                                },
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                        >
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {onDelete && (
                                                    <Tooltip title="Delete" arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onDelete(item);
                                                            }}
                                                            sx={{
                                                                bgcolor: 'error.main',
                                                                color: 'white',
                                                                '&:hover': {
                                                                    bgcolor: 'error.dark',
                                                                    transform: 'scale(1.1)'
                                                                },
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Fade>
                                    </CardContent>
                                </Card>
                            </Zoom>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '400px',
                        gap: 2
                    }}>
                        <Box sx={{
                            width: 120,
                            height: 120,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Search sx={{ fontSize: 60, color: 'text.disabled' }} />
                        </Box>
                        <Typography variant="h6" color="text.secondary">
                            {emptyMessage}
                        </Typography>
                        {searchTerm && (
                            <Button
                                variant="outlined"
                                onClick={() => setSearchTerm('')}
                                sx={{ borderRadius: '12px' }}
                            >
                                Clear Search
                            </Button>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
}
